/**
 * Recorded actions (Actions panel) for agents: storing an action and playing
 * it as one transaction.
 *
 * The UI keeps `replayAction` (one history entry per step, attributed to the
 * action), as `core/actions.ts` documents. An agent's `actions.play` is one
 * `TransactionCommand`: one undo step, all or nothing (owner decision
 * pending; ADR-009 §3.4 and docs/editor/agent-parity-status.md question 1).
 * Its steps are the very commands `replayAction` executes, built one after
 * the other against the document the previous step left (deferred steps), so
 * "the active layer" follows the replay exactly as it does in the app: it
 * starts on the explicit `layer` and moves to any layer a step creates. The
 * user's own layer selection is put back afterwards, as for every agent
 * transaction.
 *
 * What an agent replay refuses, and why:
 * - steps that read the user's pixel selection (content-aware fill, clear
 *   selected pixels; a fill without `region`) — agents never depend on hidden
 *   UI state (ADR-009 G5);
 * - duplicating a layer — it prepares asynchronously against the document as
 *   the previous step left it, which one transaction cannot do yet;
 * - non-replayable markers, as the app does.
 *
 * Rename and delete belong to the document-resources commands.
 */
import type { AdjustmentInput, LayerId, Rect } from '@maipo/agent-protocol';
import type { ActionStep, RecordedAction } from '../../core/action-types';
import { AddActionCommand, documentActions, newRecordedAction } from '../../core/actions';
import { AddLayerCommand, changed, FillRasterCommand, ContentAwareFillCommand, ClearRasterSelectionCommand, noOp, SetLayerTransformCommand, UpdateAdjustmentCommand, type Command, type CommandExecution } from '../../core/commands';
import { EMPTY_DAMAGE } from '../../core/damage';
import { invertMatrix } from '../../core/geometry';
import { createAdjustmentLayer, findLayer, type AdjustmentLayer, type EditorDocument, type Layer } from '../../core/model';
import { IntentRejection, type CompiledStep, type Diagnostic, type ValidateContext } from '../registry';
import { checkUnlocked, error, layerFor, parentKey, requireLayer, staticIds, type LockMode } from '../intents/common';
import { mergedAdjustment, validateAdjustment } from '../intents/properties';
import { defineCommand, DOCUMENT_RESOURCES } from './registry';

/** Puts the replay on a layer; undo restores the previous layer selection. */
class SetActiveLayersCommand implements Command {
  readonly label = 'Select layer';
  private before?: LayerId[];
  constructor(private readonly ids: readonly LayerId[]) {}
  retainedBytes(): number { return 128; }
  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(document.activeLayerIds) === JSON.stringify(this.ids)) return noOp();
    this.before ??= [...document.activeLayerIds];
    document.activeLayerIds = [...this.ids];
    return changed();
  }
  undo(document: EditorDocument): void { if (this.before) document.activeLayerIds = [...this.before]; }
}

function needsSelection(step: ActionStep, region: Rect | undefined): boolean {
  return step.type === 'content-aware-fill' || step.type === 'clear-selected-pixels' || (step.type === 'fill-active-raster' && !region);
}

/** Pure checks on stored steps; `strict` adds what an agent may not replay. */
function stepProblem(step: ActionStep, index: number, strict: boolean, region: Rect | undefined): Diagnostic | null {
  const where = `Step ${index + 1} (${step.type})`;
  if (step.type === 'marker') return error('COMMAND_REJECTED', `${where}: “${step.label}” was recorded as a non-replayable marker.`);
  if (step.type === 'duplicate-active-layer') return error('UNSUPPORTED_COMMAND', `${where}: duplicating a layer cannot be replayed inside one agent transaction yet.`);
  if (strict && needsSelection(step, region)) {
    return error('COMMAND_REJECTED', step.type === 'fill-active-raster'
      ? `${where} fills the user's pixel selection; pass region (document pixels) to fill instead.`
      : `${where} reads the user's pixel selection, which agents do not use.`);
  }
  if (step.type === 'transform-active-layer' && (!Array.isArray(step.transform) || step.transform.length !== 6 || !step.transform.every(Number.isFinite) || !invertMatrix(step.transform))) {
    return error('COMMAND_REJECTED', `${where}: the recorded transform is invalid.`);
  }
  return null;
}

/**
 * Locks and kinds of the start layer for the steps that run on it (until a
 * step creates a layer and the replay moves there). Pixel steps respect
 * `pixelsLocked`, transforms `positionLocked`, every step `locked` on the
 * layer or a parent group, so a locked layer refuses up front with
 * LAYER_LOCKED instead of failing mid-replay.
 */
function checkStartLayer(document: EditorDocument, layer: Layer, steps: readonly ActionStep[], context: ValidateContext, diagnostics: Diagnostic[]): void {
  const modes = new Set<LockMode>(['any']);
  for (const [index, step] of steps.entries()) {
    if (step.type === 'add-adjustment') break;
    const where = `Step ${index + 1} (${step.type})`;
    if (step.type === 'fill-active-raster' || step.type === 'content-aware-fill' || step.type === 'clear-selected-pixels') {
      modes.add('pixels');
      if (layer.kind !== 'raster' && context.policy.kinds === 'strict') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `${where} needs a raster layer; ${layer.id} is ${layer.kind}.`, layer.id));
    } else if (step.type === 'transform-active-layer') modes.add('position');
    else if (step.type === 'update-active-adjustment' && layer.kind !== 'adjustment' && context.policy.kinds === 'strict') {
      diagnostics.push(error('UNSUPPORTED_FOR_KIND', `${where} needs an adjustment layer; ${layer.id} is ${layer.kind}.`, layer.id));
    }
  }
  for (const mode of modes) {
    const before = diagnostics.length;
    checkUnlocked(document, layer, mode, diagnostics, context.policy);
    if (diagnostics.length > before) return;
  }
}

/** The native command `replayAction` runs for `step` on `active`. */
function stepCommand(document: EditorDocument, step: ActionStep, index: number, region: Rect | undefined): Command {
  const where = `Step ${index + 1} (${step.type})`;
  const id = document.activeLayerIds[0];
  const active = id ? findLayer(document.root, id) : undefined;
  if (!active) throw new IntentRejection('COMMAND_REJECTED', `${where}: no active layer is available.`);
  switch (step.type) {
    case 'fill-active-raster':
      if (active.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `${where}: layer ${active.id} is not raster.`, active.id);
      return new FillRasterCommand(active.id, structuredClone(step.definition), region ? { region } : {});
    case 'content-aware-fill':
      if (active.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `${where}: layer ${active.id} is not raster.`, active.id);
      return new ContentAwareFillCommand(active.id);
    case 'clear-selected-pixels':
      if (active.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `${where}: layer ${active.id} is not raster.`, active.id);
      return new ClearRasterSelectionCommand(active.id);
    case 'transform-active-layer':
      return new SetLayerTransformCommand(active.id, step.transform);
    case 'add-adjustment': {
      const layer = createAdjustmentLayer();
      layer.adjustment = structuredClone(step.adjustment);
      return new AddLayerCommand(layer);
    }
    case 'update-active-adjustment':
      if (active.kind !== 'adjustment') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `${where}: layer ${active.id} is not an adjustment layer.`, active.id);
      return new UpdateAdjustmentCommand(active.id, structuredClone(step.patch));
    default:
      throw new IntentRejection('COMMAND_REJECTED', `${where} cannot be replayed here.`);
  }
}

/** A native command that rejects stops the replay, like `replayAction` does; the transaction then rolls back every step. */
class ReplayStep implements Command {
  readonly label: string;
  constructor(private readonly inner: Command, private readonly index: number) { this.label = inner.label; }
  retainedBytes(): number { return this.inner.retainedBytes?.() ?? 256; }
  execute(document: EditorDocument): CommandExecution {
    const result = this.inner.execute(document);
    if (result.outcome === 'rejected') throw new IntentRejection('COMMAND_REJECTED', `Step ${this.index + 1}: ${result.reason}`);
    return result;
  }
  undo(document: EditorDocument): void { this.inner.undo(document); }
  redo(document: EditorDocument): void { if (this.inner.redo) this.inner.redo(document); else this.inner.execute(document); }
  damage() { return this.inner.damage?.() ?? EMPTY_DAMAGE; }
  dispose(): void { this.inner.dispose?.(); }
}

function actionFor(document: EditorDocument, id: string): RecordedAction | undefined {
  return documentActions(document).find((action) => action.id === id);
}

export interface PlayParams { action: string; layer?: string; region?: Rect }

export const playAction = defineCommand<PlayParams>({
  id: 'actions.play',
  group: 'resources',
  backs: ['actions.play', 'panel.actions.play'],
  // Every add-adjustment step adds a layer: a replay counts toward the creation limits like the intents it stands for.
  createsLayer: (params, document) => actionFor(document, params.action)?.steps.filter((step) => step.type === 'add-adjustment').length ?? 0,
  label: (params, document) => `Play ${actionFor(document, params.action)?.name ?? 'action'}`,
  footprint: (document, params, aliases) => {
    const ids = params.layer === undefined ? [] : staticIds([params.layer], aliases);
    const parents = ids.filter((id) => findLayer(document.root, id)).map((id) => parentKey(document, id));
    // Steps may add layers at the top of the document and move onto them.
    const keys = [...new Set([...ids, ...parents, 'root'])];
    return { reads: [...keys, DOCUMENT_RESOURCES.actions, DOCUMENT_RESOURCES.selection], writes: keys, structural: true, document: false };
  },
  validate: (document, params, context: ValidateContext) => {
    const diagnostics: Diagnostic[] = [];
    const action = actionFor(document, params.action);
    if (!action) { diagnostics.push(error('COMMAND_REJECTED', `Action ${JSON.stringify(params.action).slice(0, 80)} does not exist.`)); return diagnostics; }
    if (action.steps.length === 0) diagnostics.push(error('COMMAND_REJECTED', `Action “${action.name}” has no steps.`));
    const strict = context.policy.kinds === 'strict';
    if (params.layer === undefined) {
      if (strict) diagnostics.push(error('COMMAND_REJECTED', 'Pass layer: the layer the action starts on (agents never use the user\'s active layer).'));
      else if (!document.activeLayerIds[0]) diagnostics.push(error('COMMAND_REJECTED', 'No active layer is available.'));
    } else {
      const layer = layerFor(document, params.layer, context, diagnostics);
      if (layer) checkStartLayer(document, layer, action.steps, context, diagnostics);
    }
    action.steps.forEach((step, index) => {
      const problem = stepProblem(step, index, strict, params.region);
      if (problem) diagnostics.push(problem);
    });
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const action = actionFor(document, params.action);
    if (!action) throw new IntentRejection('COMMAND_REJECTED', `Action ${params.action} does not exist.`);
    const start = params.layer === undefined ? document.activeLayerIds[0] : requireLayer(document, params.layer, aliases).id;
    if (!start) throw new IntentRejection('COMMAND_REJECTED', 'No active layer is available.');
    const steps: CompiledStep[] = [new SetActiveLayersCommand([start])];
    action.steps.forEach((step, index) => {
      steps.push({ deferred: (current) => [new ReplayStep(stepCommand(current, step, index, params.region), index)] });
    });
    return steps;
  },
});

export interface CreateActionParams { name: string; steps: ActionStep[] }

function actionName(params: CreateActionParams): string {
  return params.name.trim().slice(0, 200) || 'Untitled action';
}

/**
 * Recorded adjustment steps hold native values and replace nested settings
 * whole (`UpdateAdjustmentCommand` spreads the patch), so a stored patch must
 * carry every field of a nested setting it names; otherwise playing it in the
 * app would write an incomplete adjustment.
 */
/** A new adjustment layer's settings (created once: making a layer draws ids, which validation must not). */
const DEFAULT_ADJUSTMENT: Readonly<AdjustmentLayer['adjustment']> = Object.freeze(createAdjustmentLayer().adjustment);

const COMPLETE_NESTED: Record<'curves' | 'gradientMap' | 'blackWhite', readonly string[]> = {
  curves: ['rgb', 'red', 'green', 'blue'],
  gradientMap: ['enabled', 'reverse', 'stops'],
  blackWhite: Object.keys(DEFAULT_ADJUSTMENT.blackWhite ?? {}),
};

function incompleteNested(patch: AdjustmentInput): string | null {
  for (const [key, fields] of Object.entries(COMPLETE_NESTED)) {
    const value = patch[key as keyof typeof COMPLETE_NESTED] as Record<string, unknown> | undefined;
    if (value !== undefined && fields.some((field) => value[field] === undefined)) return `patch.${key} must set ${fields.join(', ')}: a recorded step replaces it whole.`;
  }
  return null;
}

/** The step as the app records it: adjustments normalised to native values (a new adjustment is complete). */
function nativeStep(step: ActionStep): ActionStep {
  const base = structuredClone(DEFAULT_ADJUSTMENT) as AdjustmentLayer['adjustment'];
  if (step.type === 'add-adjustment') return { type: 'add-adjustment', adjustment: { ...base, ...mergedAdjustment(base, step.adjustment as unknown as AdjustmentInput) } };
  if (step.type === 'update-active-adjustment') return { type: 'update-active-adjustment', patch: mergedAdjustment(base, step.patch as unknown as AdjustmentInput) };
  return structuredClone(step);
}

export const createAction = defineCommand<CreateActionParams>({
  id: 'actions.create',
  group: 'resources',
  backs: ['action.record-action', 'panel.actions.record-stop'],
  label: (params) => `Add action ${actionName(params)}`,
  footprint: () => ({ reads: [DOCUMENT_RESOURCES.actions], writes: [DOCUMENT_RESOURCES.actions], structural: false, document: false }),
  validate: (_document, params) => {
    const diagnostics: Diagnostic[] = [];
    params.steps.forEach((step, index) => {
      if (step.type === 'transform-active-layer' && !invertMatrix(step.transform)) diagnostics.push(error('COMMAND_REJECTED', `Step ${index + 1}: the transform must be invertible.`));
      if (step.type === 'add-adjustment' || step.type === 'update-active-adjustment') {
        const adjustment = (step.type === 'add-adjustment' ? step.adjustment : step.patch) as unknown as AdjustmentInput;
        diagnostics.push(...validateAdjustment(structuredClone(DEFAULT_ADJUSTMENT) as AdjustmentLayer['adjustment'], adjustment).map((item) => ({ ...item, message: `Step ${index + 1}: ${item.message}` })));
        const incomplete = step.type === 'update-active-adjustment' ? incompleteNested(adjustment) : null;
        if (incomplete) diagnostics.push(error('COMMAND_REJECTED', `Step ${index + 1}: ${incomplete}`));
      }
    });
    return diagnostics;
  },
  compile: (_document, params) => [new AddActionCommand(newRecordedAction(params.name, params.steps.map(nativeStep)))],
});

export const ACTION_COMMANDS = [createAction, playAction] as const;
